import { initApp } from './app';
import type { BeforeInstallPromptEvent, ServiceWorkerState } from './pwa/types';

type AppInstance = ReturnType<typeof initApp>;

declare global {
  interface Window {
    letterRainApp?: AppInstance;
  }
}

let currentApp: AppInstance | null = null;
let pwaEventsBound = false;
let controllerChangeBound = false;
let attemptedServiceWorkerRegistration = false;

document.addEventListener('DOMContentLoaded', () => {
  const mount = document.querySelector<HTMLElement>('#app');
  if (!mount) {
    console.warn('Letter Rain: mount element #app not found.');
    return;
  }

  const app = initApp(mount);
  window.letterRainApp = app;
  setupPwaBridge(app);
});

function setupPwaBridge(app: AppInstance): void {
  currentApp = app;

  if (!pwaEventsBound) {
    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      currentApp?.pwa.setInstallPrompt(event as BeforeInstallPromptEvent);
    });

    window.addEventListener('appinstalled', () => {
      if (!currentApp) {
        return;
      }
      currentApp.pwa.markInstalled();
      currentApp.pwa.setInstallPrompt(null);
    });

    pwaEventsBound = true;
  }

  if (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone
  ) {
    app.pwa.markInstalled();
    app.pwa.setInstallPrompt(null);
  }

  if (!('serviceWorker' in navigator)) {
    app.pwa.setServiceWorkerState('unsupported');
    return;
  }

  if (navigator.serviceWorker.controller) {
    app.pwa.setServiceWorkerState('active');
  } else {
    app.pwa.setServiceWorkerState('pending');
  }

  const updateState = (state: ServiceWorkerState) => {
    currentApp?.pwa.setServiceWorkerState(state);
  };

  if (!controllerChangeBound) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      updateState('active');
    });
    controllerChangeBound = true;
  }

  if (attemptedServiceWorkerRegistration) {
    return;
  }

  attemptedServiceWorkerRegistration = true;

  const register = async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });

      if (!registration) {
        updateState('blocked');
        return;
      }

      if (registration.active) {
        updateState('active');
      } else if (registration.installing || registration.waiting) {
        const attach = (worker: ServiceWorker | null) => {
          worker?.addEventListener('statechange', () => {
            if (registration.active) {
              updateState('active');
            }
          });
        };
        attach(registration.installing ?? null);
        attach(registration.waiting ?? null);
      }

      navigator.serviceWorker.ready
        .then(() => updateState('active'))
        .catch((error) => {
          console.warn('Letter Rain: service worker ready blocked', error);
          updateState('blocked');
        });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.warn('Letter Rain: service worker registration failed', error);
      if (error instanceof DOMException && error.name === 'SecurityError') {
        updateState('blocked');
      } else if (message.toLowerCase().includes('blocked')) {
        updateState('blocked');
      } else {
        updateState('error');
      }
    }
  };

  if (document.readyState === 'complete') {
    register().catch(() => undefined);
  } else {
    window.addEventListener('load', () => register().catch(() => undefined), {
      once: true,
    });
  }
}
